/**
 * Sorts objects by multiple properties, trying first one, then the next if values are equal.
 * For string properties, uses sortByProperty internal logic when possible.
 * For 'defined-ness' of properties, prioritizes items where the property is defined.
 *
 * @param properties - Array of properties to sort by, in order of priority
 * @returns A comparator function for Array.prototype.sort()
 */
export function sortByMultipleProperties<T extends object, K extends keyof T>(
  ...properties: K[]
): (a: T, b: T) => number {
  return (a, b): number => {
    for (const prop of properties) {
      // Prioritize defined values
      if (a[prop] !== undefined && b[prop] === undefined) return -1;
      if (a[prop] === undefined && b[prop] !== undefined) return 1;
      if (a[prop] === undefined && b[prop] === undefined) continue;

      // At this point, both values are defined
      const aVal = a[prop] as NonNullable<T[K]>;
      const bVal = b[prop] as NonNullable<T[K]>;

      // For string properties, use string comparison logic
      if (typeof aVal === "string" && typeof bVal === "string") {
        // Apply the same logic that sortByProperty uses
        const aStr = aVal;
        const bStr = bVal;

        // Check if strings start with a dot (non-letter)
        const isLetter = (str: string) => str[0] !== ".";
        const aIsLetter = isLetter(aStr);
        const bIsLetter = isLetter(bStr);

        if (aIsLetter && !bIsLetter) return -1;
        if (!aIsLetter && bIsLetter) return 1;

        // Normal string comparison
        const comparison = aStr.localeCompare(bStr);
        if (comparison !== 0) return comparison;
      }
      // For other types, do a simple comparison
      else {
        if (aVal < bVal) return -1;
        if (aVal > bVal) return 1;
      }
    }
    return 0;
  };
}
